import { apiClient } from '@/shared/lib/axios';
import { clinicApiPath } from '@/shared/lib/apiPath';

export interface RezeptApplicationCountItem {
  id: number;
  patient_id: number;
  clinic_id: number;
  service_type: string;
  rezept_yearmonth: number;
  rezept_application_count: number;
  rezept_related_application_count: number;
  created_at?: string;
  updated_at?: string;
}

export interface RezeptApplicationCountListResponse {
  success: boolean;
  data: {
    patient: {
      id: number;
      serial_number: number | string;
      current_patient_information?: { name?: string; name_kana?: string };
    };
    rezept_application_counts: RezeptApplicationCountItem[];
  };
}

/**
 * Fetch rezept application counts list for a patient (申請回数一覧).
 * API: GET /api/v1/clinic/{site_type}/{clinic_number}/patients/{patientId}/rezept-application-counts
 */
export async function fetchRezeptApplicationCountList(
  siteType: string,
  clinicNumber: string,
  patientId: string,
): Promise<{ patient: RezeptApplicationCountListResponse['data']['patient']; list: RezeptApplicationCountItem[] }> {
  const response = await apiClient.get<RezeptApplicationCountListResponse>(
    clinicApiPath(siteType, clinicNumber, `/patients/${patientId}/rezept-application-counts`),
  );
  const data = response.data.data;
  return {
    patient: data.patient,
    list: data.rezept_application_counts ?? [],
  };
}

/** Single item response */
export interface RezeptApplicationCountDetailResponse {
  success: boolean;
  data: RezeptApplicationCountItem;
}

export async function fetchRezeptApplicationCountOne(
  siteType: string,
  clinicNumber: string,
  patientId: string,
  id: string,
): Promise<RezeptApplicationCountItem> {
  const response = await apiClient.get<RezeptApplicationCountDetailResponse>(
    clinicApiPath(siteType, clinicNumber, `/patients/${patientId}/rezept-application-counts/${id}`),
  );
  return response.data.data;
}

export interface RezeptApplicationCountPayload {
  rezept_yearmonth: number;
  rezept_application_count?: number;
  rezept_related_application_count?: number;
}

export async function createRezeptApplicationCount(
  siteType: string,
  clinicNumber: string,
  patientId: string,
  payload: RezeptApplicationCountPayload,
): Promise<RezeptApplicationCountItem> {
  const response = await apiClient.post<RezeptApplicationCountDetailResponse>(
    clinicApiPath(siteType, clinicNumber, `/patients/${patientId}/rezept-application-counts`),
    payload,
  );
  return response.data.data;
}

export async function updateRezeptApplicationCount(
  siteType: string,
  clinicNumber: string,
  patientId: string,
  id: number,
  payload: RezeptApplicationCountPayload,
): Promise<RezeptApplicationCountItem> {
  const response = await apiClient.put<RezeptApplicationCountDetailResponse>(
    clinicApiPath(siteType, clinicNumber, `/patients/${patientId}/rezept-application-counts/${id}`),
    payload,
  );
  return response.data.data;
}

export async function deleteRezeptApplicationCount(
  siteType: string,
  clinicNumber: string,
  patientId: string,
  id: number,
): Promise<void> {
  await apiClient.delete(
    clinicApiPath(siteType, clinicNumber, `/patients/${patientId}/rezept-application-counts/${id}`),
  );
}
